import React from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';

interface TranslationsStatsProps {
	templateResult: any;
	formatLanguageKey: (key: string) => string;
}

const TranslationsStats: React.FC<TranslationsStatsProps> = ({
	templateResult,
	formatLanguageKey,
}) => {
	if (!templateResult || !Array.isArray(templateResult.runs)) {
		return null;
	}

	const { templateName, runs } = templateResult;

	const metrics = [
		{ key: 'fragmentStats', label: 'Fragment Length' },
		{ key: 'quality', label: 'Translation Quality' },
		{ key: 'translationLatency', label: 'Translation Latency' },
		{ key: 'translationProviderLatency', label: 'Provider Latency' },
	];

	function limitDecimals(input: number | string, limit: number): number {
		const num = typeof input === 'string' ? parseFloat(input) : input;

		if (isNaN(num)) {
			throw new Error(`Invalid number input: ${input}`);
		}
		const decimalPoints = limit * 10;
		return Math.round(num * decimalPoints) / decimalPoints;
	}

	const sortedRuns = [...runs].sort((a, b) => {
		if (a.key < b.key) {
			return -1;
		}
		if (a.key > b.key) {
			return 1;
		}
		return 0;
	});

	return (
		<Box borderWidth={1} borderRadius="lg" overflow="hidden" mb={6}>
			<Flex bg="#0b9cbb" color="white" p={3}>
				<Text fontWeight="bold" fontSize="lg">
					{templateName || 'Unnamed Template'}
				</Text>
			</Flex>
			<Box overflowX="auto">
				<table style={{ width: '100%', borderCollapse: 'collapse' }}>
					<thead>
						<tr style={{ backgroundColor: '#adedf4' }}>
							<th
								rowSpan={2}
								style={{
									textAlign: 'left',
									padding: '8px',
									border: '1px solid #ccc',
									minWidth: '120px',
								}}
							>
								Language
							</th>
							{metrics.map(metric => (
								<th
									key={metric.key}
									colSpan={3}
									style={{
										textAlign: 'center',
										padding: '8px',
										border: '1px solid #ccc',
										backgroundColor: '#c9eef7',
										fontWeight: 600,
									}}
								>
									{metric.label}
								</th>
							))}
						</tr>
						<tr style={{ backgroundColor: '#e6f7fa' }}>
							{metrics.map(metric => (
								<React.Fragment key={metric.key}>
									<th
										key={`${metric.key}_min`}
										style={{
											padding: '6px',
											border: '1px solid #ccc',
											fontWeight: 'normal',
											textAlign: 'center',
										}}
									>
										Min
									</th>
									<th
										key={`${metric.key}_avg`}
										style={{
											padding: '6px',
											border: '1px solid #ccc',
											fontWeight: 'normal',
											textAlign: 'center',
										}}
									>
										Avg
									</th>
									<th
										key={`${metric.key}_max`}
										style={{
											padding: '6px',
											border: '1px solid #ccc',
											fontWeight: 'normal',
											textAlign: 'center',
										}}
									>
										Max
									</th>
								</React.Fragment>
							))}
						</tr>
					</thead>
					<tbody>
						{sortedRuns.map((r: any) => {
							const v = r.value;
							return (
								<tr key={r.key} style={{ backgroundColor: '#fff' }}>
									<td
										style={{
											padding: '8px',
											border: '1px solid #ccc',
											fontWeight: 500,
											textTransform: 'uppercase',
										}}
									>
										{formatLanguageKey(r.key)}
									</td>
									<td
										style={{
											padding: '8px',
											border: '1px solid #ccc',
											textAlign: 'center',
										}}
									>
										{limitDecimals(v.fragmentStats?.min || 0, 4) ?? '-'}
									</td>
									<td
										style={{
											padding: '8px',
											border: '1px solid #ccc',
											textAlign: 'center',
										}}
									>
										{limitDecimals(v.fragmentStats?.avg || 0, 4) ?? '-'}
									</td>
									<td
										style={{
											padding: '8px',
											border: '1px solid #ccc',
											textAlign: 'center',
										}}
									>
										{limitDecimals(v.fragmentStats?.max || 0, 4) ?? '-'}
									</td>
									<td
										style={{
											padding: '8px',
											border: '1px solid #ccc',
											textAlign: 'center',
										}}
									>
										{limitDecimals(v.quality?.min || 0, 4) ?? '-'}
									</td>
									<td
										style={{
											padding: '8px',
											border: '1px solid #ccc',
											textAlign: 'center',
										}}
									>
										{limitDecimals(v.quality?.avg || 0, 4) ?? '-'}
									</td>
									<td
										style={{
											padding: '8px',
											border: '1px solid #ccc',
											textAlign: 'center',
										}}
									>
										{limitDecimals(v.quality?.max || 0, 4) ?? '-'}
									</td>
									<td
										style={{
											padding: '8px',
											border: '1px solid #ccc',
											textAlign: 'center',
										}}
									>
										{limitDecimals(v?.translationLatency?.min || 0, 4) ?? '-'}
									</td>
									<td
										style={{
											padding: '8px',
											border: '1px solid #ccc',
											textAlign: 'center',
										}}
									>
										{limitDecimals(v?.translationLatency?.avg || 0, 4) ?? '-'}
									</td>
									<td
										style={{
											padding: '8px',
											border: '1px solid #ccc',
											textAlign: 'center',
										}}
									>
										{limitDecimals(v?.translationLatency?.max || 0, 4) ?? '-'}
									</td>
									{/* Provider Latency */}
									<td
										style={{
											padding: '8px',
											border: '1px solid #ccc',
											textAlign: 'center',
										}}
									>
										{limitDecimals(
											v?.translationProviderLatency?.min || 0,
											4,
										) ?? '-'}
									</td>
									<td
										style={{
											padding: '8px',
											border: '1px solid #ccc',
											textAlign: 'center',
										}}
									>
										{limitDecimals(
											v?.translationProviderLatency?.avg || 0,
											4,
										) ?? '-'}
									</td>
									<td
										style={{
											padding: '8px',
											border: '1px solid #ccc',
											textAlign: 'center',
										}}
									>
										{limitDecimals(
											v?.translationProviderLatency?.max || 0,
											4,
										) ?? '-'}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</Box>
		</Box>
	);
};

export default TranslationsStats;
